import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { MoonLoader } from "react-spinners";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import { publicApi } from "../../api/http";
import GridListViewToggle from "../../components/GridListViewToggle/GridListViewToggle";
import {
    anonymizeProspects,
    applyProspectFilters,
    collectProspectLeagues,
    createRandomDraftOrder,
    DEFAULT_LIST_FILTERS,
    DEFAULT_PROSPECT_SORT,
    formatPointsPerGame,
    formatSavePct,
    getNextSortDirection,
    getTeamNames,
    pickCpuProspect,
    pickNumberToSlot,
    PROSPECT_POOL_SIZE,
    sortProspects,
    TEAM_COUNT,
    totalPicks,
    USER_TEAM_INDEX,
} from "../../utils/blindDraft";
import ProspectBoard, { ProspectStatsBreakdown } from "./ProspectBoard";
import DraftResults from "./DraftResults";
import DraftOrderReveal from "./DraftOrderReveal";
import PickTurnModal from "./PickTurnModal";
import "./BlindDraft.css";

const PHASE = {
    INTRO: "intro",
    LOADING: "loading",
    BROWSE: "browse",
    DRAFT_ORDER: "draft_order",
    DRAFT: "draft",
    RESULTS: "results",
};

function ProspectBoardToolbar({
    search,
    onSearchChange,
    positionFilter,
    onPositionFilterChange,
    positions,
    listFilters,
    onListFiltersChange,
    leagues,
    viewMode,
    onViewModeChange,
    starredCount,
    children,
}) {
    const updateLeagueFilter = (key, value) => {
        onListFiltersChange({ ...listFilters, [key]: value });
    };

    return (
        <div className="blind-draft-toolbar">
            <input
                className="blind-draft-search"
                placeholder="Search prospects…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
            />
            {viewMode === "grid" && (
                <select
                    className="blind-draft-filter"
                    value={positionFilter}
                    onChange={(e) => onPositionFilterChange(e.target.value)}
                >
                    {positions.map((pos) => (
                        <option key={pos} value={pos}>{pos}</option>
                    ))}
                </select>
            )}
            {viewMode === "grid" && leagues?.length > 1 && (
                <>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.league}
                        onChange={(e) => updateLeagueFilter("league", e.target.value)}
                    >
                        {leagues.map((league) => (
                            <option key={league} value={league}>
                                {league === "ALL" ? "Any league" : league}
                            </option>
                        ))}
                    </select>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.leaguePlayed}
                        onChange={(e) => updateLeagueFilter("leaguePlayed", e.target.value)}
                        disabled={listFilters.league === "ALL"}
                    >
                        <option value="YES">Played in</option>
                        <option value="NO">Not in</option>
                    </select>
                </>
            )}
            <GridListViewToggle value={viewMode} onChange={onViewModeChange} ariaLabel="Prospect layout" />
            {starredCount > 0 && (
                <span className="blind-draft-starred-count">
                    <StarRoundedIcon fontSize="inherit" />
                    {starredCount} starred
                </span>
            )}
            {children}
        </div>
    );
}

function BlindDraft() {
    const [phase, setPhase] = useState(PHASE.INTRO);
    const [prospects, setProspects] = useState([]);
    const [identityMap, setIdentityMap] = useState(new Map());
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [positionFilter, setPositionFilter] = useState("ALL");
    const [listFilters, setListFilters] = useState(DEFAULT_LIST_FILTERS);
    const [sortConfig, setSortConfig] = useState(DEFAULT_PROSPECT_SORT);
    const [viewMode, setViewMode] = useState("list");
    const [starredIds, setStarredIds] = useState(() => new Set());
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [draftedIds, setDraftedIds] = useState([]);
    const [picks, setPicks] = useState([]);
    const [currentPick, setCurrentPick] = useState(1);
    const [draftOrder, setDraftOrder] = useState([]);
    const [isCpuThinking, setIsCpuThinking] = useState(false);
    const [showPickTurnModal, setShowPickTurnModal] = useState(false);
    const cpuTimerRef = useRef(null);
    const wasUserTurnRef = useRef(false);
    const teamNames = useMemo(() => getTeamNames(), []);

    const availableProspects = useMemo(
        () => prospects.filter((p) => !draftedIds.includes(p.anonId)),
        [prospects, draftedIds]
    );

    const positions = useMemo(() => {
        const set = new Set(prospects.map((p) => p.position).filter(Boolean));
        return ["ALL", ...Array.from(set).sort()];
    }, [prospects]);

    const countries = useMemo(() => {
        const set = new Set(prospects.map((p) => p.country).filter(Boolean));
        return ["ALL", ...Array.from(set).sort()];
    }, [prospects]);

    const leagues = useMemo(
        () => ["ALL", ...collectProspectLeagues(prospects)],
        [prospects]
    );

    const prepareProspects = useCallback((list) => {
        const filters = viewMode === "list"
            ? { search, ...listFilters }
            : {
                search,
                position: positionFilter,
                country: "ALL",
                type: "ALL",
                starred: "ALL",
                league: listFilters.league,
                leaguePlayed: listFilters.leaguePlayed,
                minGp: "",
                minPts: "",
                minPpg: "",
                minSv: "",
                maxGaa: "",
            };

        const filtered = applyProspectFilters(list, filters, starredIds);
        return sortProspects(filtered, sortConfig.key, sortConfig.direction, starredIds);
    }, [viewMode, search, listFilters, positionFilter, starredIds, sortConfig]);

    const filteredProspects = useMemo(
        () => prepareProspects(availableProspects),
        [availableProspects, prepareProspects]
    );

    const browseProspects = useMemo(
        () => prepareProspects(prospects),
        [prospects, prepareProspects]
    );

    const handleSort = useCallback((sortKey) => {
        setSortConfig((current) => ({
            key: sortKey,
            direction: getNextSortDirection(sortKey, current.key, current.direction),
        }));
    }, []);

    const clearListFilters = useCallback(() => {
        setListFilters(DEFAULT_LIST_FILTERS);
    }, []);

    const handleViewModeChange = useCallback((mode) => {
        if (mode === "grid") {
            setPositionFilter(listFilters.position);
        } else {
            setListFilters((current) => ({ ...current, position: positionFilter }));
        }
        setViewMode(mode);
    }, [listFilters.position, positionFilter]);

    const effectiveDraftOrder = draftOrder.length
        ? draftOrder
        : Array.from({ length: TEAM_COUNT }, (_, index) => index);

    const { teamIndex: onClockTeamIndex, round: onClockRound } = pickNumberToSlot(
        currentPick,
        effectiveDraftOrder
    );
    const isUserTurn = onClockTeamIndex === USER_TEAM_INDEX && !isCpuThinking;
    const draftComplete = currentPick > totalPicks();

    const userPicks = useMemo(
        () => picks.filter((pick) => pick.teamIndex === USER_TEAM_INDEX),
        [picks]
    );

    const toggleStar = useCallback((anonId) => {
        setStarredIds((prev) => {
            const next = new Set(prev);
            if (next.has(anonId)) {
                next.delete(anonId);
            } else {
                next.add(anonId);
            }
            return next;
        });
    }, []);

    const loadProspects = useCallback(async () => {
        setPhase(PHASE.LOADING);
        setError("");
        try {
            const response = await publicApi.get("/draft-game/prospects", { params: { count: PROSPECT_POOL_SIZE } });
            const rawPlayers = response.data?.players || [];
            if (!rawPlayers.length) {
                throw new Error("No eligible prospects found.");
            }
            const { prospects: anonProspects, identityMap: map } = anonymizeProspects(rawPlayers);
            setProspects(anonProspects);
            setIdentityMap(map);
            setDraftedIds([]);
            setPicks([]);
            setCurrentPick(1);
            setDraftOrder([]);
            setStarredIds(new Set());
            setViewMode("list");
            setListFilters(DEFAULT_LIST_FILTERS);
            setSortConfig(DEFAULT_PROSPECT_SORT);
            setPositionFilter("ALL");
            setSearch("");
            setPhase(PHASE.BROWSE);
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Failed to load prospects.");
            setPhase(PHASE.INTRO);
        }
    }, []);

    const makePick = useCallback((prospect, teamIndex, pickNumber) => {
        const slot = pickNumberToSlot(pickNumber, draftOrder);
        setDraftedIds((prev) => [...prev, prospect.anonId]);
        setPicks((prev) => [
            ...prev,
            {
                pickNumber,
                teamIndex,
                teamName: teamNames[teamIndex],
                prospect,
                round: slot.round,
            },
        ]);
        setCurrentPick(pickNumber + 1);
        setSelectedProspect(null);
    }, [teamNames, draftOrder]);

    useEffect(() => {
        if (phase !== PHASE.DRAFT || draftComplete || !draftOrder.length) return undefined;

        const { teamIndex } = pickNumberToSlot(currentPick, draftOrder);
        if (teamIndex === USER_TEAM_INDEX) {
            setIsCpuThinking(false);
            return undefined;
        }

        setIsCpuThinking(true);
        cpuTimerRef.current = setTimeout(() => {
            const drafted = new Set(draftedIds);
            const pool = prospects.filter((p) => !drafted.has(p.anonId));
            const choice = pickCpuProspect(pool);
            if (choice) {
                makePick(choice, teamIndex, currentPick);
            } else {
                setIsCpuThinking(false);
            }
        }, 250 + Math.random() * 200);

        return () => {
            if (cpuTimerRef.current) clearTimeout(cpuTimerRef.current);
        };
    }, [phase, currentPick, draftComplete, draftedIds, prospects, makePick, draftOrder]);

    useEffect(() => {
        if (phase === PHASE.DRAFT && draftComplete) {
            const timer = setTimeout(() => setPhase(PHASE.RESULTS), 600);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [phase, draftComplete]);

    useEffect(() => {
        if (phase !== PHASE.DRAFT || draftComplete) {
            setShowPickTurnModal(false);
            wasUserTurnRef.current = false;
            return;
        }

        if (isUserTurn && !wasUserTurnRef.current) {
            setShowPickTurnModal(true);
        }

        wasUserTurnRef.current = isUserTurn;
    }, [isUserTurn, phase, draftComplete]);

    const handleUserDraft = (prospect) => {
        if (!isUserTurn || draftedIds.includes(prospect.anonId)) return;
        setShowPickTurnModal(false);
        makePick(prospect, USER_TEAM_INDEX, currentPick);
        setIsCpuThinking(false);
    };

    const dismissPickTurnModal = useCallback(() => {
        setShowPickTurnModal(false);
    }, []);

    const startDraft = () => {
        setDraftOrder(createRandomDraftOrder());
        setSearch("");
        setPositionFilter("ALL");
        setListFilters(DEFAULT_LIST_FILTERS);
        setSelectedProspect(null);
        setPhase(PHASE.DRAFT_ORDER);
    };

    const beginDraft = () => {
        setPhase(PHASE.DRAFT);
        setCurrentPick(1);
        setDraftedIds([]);
        setPicks([]);
    };

    const selectedIsStarred = selectedProspect ? starredIds.has(selectedProspect.anonId) : false;
    const selectedTotals = selectedProspect?.draftTotals;

    return (
        <div className="blind-draft">
            <Helmet>
                <title>Blind Draft Game | Hockey Pipeline</title>
                <meta
                    name="description"
                    content="Scout anonymized prospects and test your eye in a simulated NHL draft."
                />
            </Helmet>

            <div className="blind-draft-shell">
                <header className="blind-draft-brand">
                    <img src="/logo@4x.png" alt="Hockey Pipeline" />
                    <div className="blind-draft-brand-copy">
                        <h1>Hockey Pipeline</h1>
                        <p>Blind Draft Challenge</p>
                    </div>
                </header>

                {phase === PHASE.INTRO && (
                    <section className="blind-draft-hero">
                        <h2>Can you outscout the room?</h2>
                        <p>
                            Review {PROSPECT_POOL_SIZE} anonymized prospect profiles — stats only, no team names.
                            Study the numbers, run your board, then draft four players in a straight-order
                            mock draft against seven CPU teams.
                        </p>
                        {error && <p style={{ color: "var(--negative)", marginBottom: 16 }}>{error}</p>}
                        <button type="button" className="blind-draft-cta" onClick={loadProspects}>
                            Start Scouting
                        </button>
                    </section>
                )}

                {phase === PHASE.LOADING && (
                    <div className="blind-draft-loading">
                        <MoonLoader color="var(--brand)" size={40} />
                        <p>Building your prospect pool…</p>
                    </div>
                )}

                {phase === PHASE.BROWSE && (
                    <>
                        <div className="blind-draft-phase-header">
                            <div>
                                <h2>Prospect Board</h2>
                                <p>{prospects.length} anonymized players · draft-season stats combined across leagues</p>
                            </div>
                            <ProspectBoardToolbar
                                search={search}
                                onSearchChange={setSearch}
                                positionFilter={positionFilter}
                                onPositionFilterChange={setPositionFilter}
                                positions={positions}
                                listFilters={listFilters}
                                onListFiltersChange={setListFilters}
                                leagues={leagues}
                                viewMode={viewMode}
                                onViewModeChange={handleViewModeChange}
                                starredCount={starredIds.size}
                            >
                                <button type="button" className="blind-draft-cta" onClick={startDraft}>
                                    Enter Draft
                                </button>
                            </ProspectBoardToolbar>
                        </div>
                        <ProspectBoard
                            prospects={browseProspects}
                            viewMode={viewMode}
                            draftedIds={draftedIds}
                            starredIds={starredIds}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            listFilters={listFilters}
                            onListFiltersChange={setListFilters}
                            countries={countries}
                            positions={positions}
                            leagues={leagues}
                            onClearListFilters={clearListFilters}
                            onToggleStar={toggleStar}
                            onOpen={setSelectedProspect}
                        />
                    </>
                )}

                {phase === PHASE.DRAFT_ORDER && (
                    <DraftOrderReveal
                        draftOrder={draftOrder}
                        teamNames={teamNames}
                        onBeginDraft={beginDraft}
                        onBack={() => setPhase(PHASE.BROWSE)}
                    />
                )}

                {phase === PHASE.DRAFT && !draftComplete && (
                    <>
                        {showPickTurnModal && (
                            <PickTurnModal
                                pickNumber={currentPick}
                                round={onClockRound}
                                onDismiss={dismissPickTurnModal}
                            />
                        )}
                        <div className="blind-draft-board-layout blind-draft-board-layout--three-col">
                        <aside className="blind-draft-sidebar blind-draft-sidebar--left">
                            <div className={`blind-draft-on-clock ${isUserTurn ? "is-user" : "is-waiting"}`}>
                                <h4>{isUserTurn ? "Your pick" : "On the clock"}</h4>
                                <p className="blind-draft-on-clock-team">
                                    {isUserTurn
                                        ? "You're on the clock"
                                        : teamNames[onClockTeamIndex]}
                                </p>
                                <p className="blind-draft-on-clock-action">
                                    {isUserTurn
                                        ? "Select a prospect and click Draft"
                                        : isCpuThinking
                                            ? "Making their pick…"
                                            : "Waiting to pick…"}
                                </p>
                                <p className="blind-draft-on-clock-meta">
                                    Round {onClockRound} · Pick {currentPick} of {totalPicks()}
                                </p>
                            </div>
                            {starredIds.size > 0 && (
                                <p className="blind-draft-sidebar-note">
                                    <StarRoundedIcon fontSize="inherit" />
                                    Starred players are marked on your board
                                </p>
                            )}
                            <div className="blind-draft-pick-log">
                                <h4 className="blind-draft-sidebar-title">Live Draft Feed</h4>
                                {[...picks].reverse().map((pick) => (
                                    <div
                                        key={pick.pickNumber}
                                        className={`blind-draft-pick-item ${pick.teamIndex === USER_TEAM_INDEX ? "is-user" : ""}`}
                                    >
                                        <div className="blind-draft-pick-num">{pick.pickNumber}</div>
                                        <div className="blind-draft-pick-copy">
                                            <strong>{pick.prospect.fakeName}</strong>
                                            <span>{pick.teamName} · {pick.prospect.position} · R{pick.round}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </aside>
                        <div className={`blind-draft-board-main ${isUserTurn ? "is-your-turn" : "is-waiting"}`}>
                            {isUserTurn ? (
                                <div className="blind-draft-your-turn-banner" role="status" aria-live="polite">
                                    <span className="blind-draft-your-turn-dot" aria-hidden="true" />
                                    <div>
                                        <strong>Your turn to draft</strong>
                                        <span>
                                            Round {onClockRound}, pick {currentPick}
                                            {" — "}
                                            choose a player and hit Draft
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="blind-draft-waiting-banner" role="status" aria-live="polite">
                                    <strong>{teamNames[onClockTeamIndex]} is picking</strong>
                                    <span>Round {onClockRound}, pick {currentPick}</span>
                                </div>
                            )}
                            <div className="blind-draft-phase-header">
                                <div>
                                    <h2>Available Prospects</h2>
                                    <p>{availableProspects.length} remaining</p>
                                </div>
                                <ProspectBoardToolbar
                                    search={search}
                                    onSearchChange={setSearch}
                                    positionFilter={positionFilter}
                                    onPositionFilterChange={setPositionFilter}
                                    positions={positions}
                                    listFilters={listFilters}
                                    onListFiltersChange={setListFilters}
                                    leagues={leagues}
                                    viewMode={viewMode}
                                    onViewModeChange={handleViewModeChange}
                                    starredCount={starredIds.size}
                                />
                            </div>
                            <ProspectBoard
                                prospects={filteredProspects}
                                viewMode={viewMode}
                                draftedIds={draftedIds}
                                starredIds={starredIds}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                                listFilters={listFilters}
                                onListFiltersChange={setListFilters}
                                countries={countries}
                                positions={positions}
                                leagues={leagues}
                                onClearListFilters={clearListFilters}
                                onToggleStar={toggleStar}
                                onOpen={setSelectedProspect}
                                onDraft={handleUserDraft}
                                canDraft={isUserTurn}
                            />
                        </div>
                        <aside className="blind-draft-sidebar blind-draft-sidebar--right">
                            <h4 className="blind-draft-sidebar-title">Your Draft Class</h4>
                            <p className="blind-draft-sidebar-subtitle">
                                {userPicks.length} of 4 picks made
                            </p>
                            <div className="blind-draft-my-picks">
                                {userPicks.length === 0 ? (
                                    <p className="blind-draft-my-picks-empty">Your selections will appear here.</p>
                                ) : (
                                    [...userPicks].reverse().map((pick) => (
                                        <div key={pick.pickNumber} className="blind-draft-my-pick-card">
                                            <div className="blind-draft-my-pick-top">
                                                <span className="blind-draft-badge">R{pick.round}</span>
                                                <span className="blind-draft-my-pick-num">#{pick.pickNumber}</span>
                                            </div>
                                            <strong>{pick.prospect.fakeName}</strong>
                                            <span className="blind-draft-my-pick-position">{pick.prospect.position}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </aside>
                        </div>
                    </>
                )}

                {phase === PHASE.RESULTS && (
                    <DraftResults
                        userPicks={userPicks}
                        picks={picks}
                        teamNames={teamNames}
                        identityMap={identityMap}
                        onPlayAgain={loadProspects}
                    />
                )}

                {selectedProspect && (
                    <div
                        className="blind-draft-detail-overlay"
                        onClick={() => setSelectedProspect(null)}
                        onKeyDown={(e) => e.key === "Escape" && setSelectedProspect(null)}
                        role="presentation"
                    >
                        <div
                            className="blind-draft-detail-panel"
                            onClick={(e) => e.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-label={`${selectedProspect.fakeName} stats`}
                        >
                            <div className="blind-draft-detail-header">
                                <div>
                                    <div className="blind-draft-detail-title-row">
                                        <h3>{selectedProspect.fakeName}</h3>
                                        <button
                                            type="button"
                                            className={`blind-draft-star-btn blind-draft-star-btn--inline ${selectedIsStarred ? "is-starred" : ""}`}
                                            aria-label={selectedIsStarred ? "Unstar prospect" : "Star prospect"}
                                            onClick={() => toggleStar(selectedProspect.anonId)}
                                        >
                                            {selectedIsStarred ? (
                                                <StarRoundedIcon fontSize="small" />
                                            ) : (
                                                <StarBorderRoundedIcon fontSize="small" />
                                            )}
                                        </button>
                                    </div>
                                    <p style={{ margin: "4px 0 0", color: "var(--text-secondary)" }}>
                                        {selectedProspect.position} · {selectedProspect.country}
                                        {selectedIsStarred && (
                                            <span className="blind-draft-watch-label"> · Watchlist</span>
                                        )}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="blind-draft-close"
                                    onClick={() => setSelectedProspect(null)}
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="blind-draft-info-grid">
                                <div className="blind-draft-info-item">
                                    <span>Height</span>
                                    <strong>{selectedProspect.height}</strong>
                                </div>
                                <div className="blind-draft-info-item">
                                    <span>Weight</span>
                                    <strong>{selectedProspect.weight}</strong>
                                </div>
                                <div className="blind-draft-info-item">
                                    <span>Shoots</span>
                                    <strong>{selectedProspect.handedness}</strong>
                                </div>
                                <div className="blind-draft-info-item">
                                    <span>GP</span>
                                    <strong>{selectedTotals?.gamesPlayed ?? "—"}</strong>
                                </div>
                                {selectedProspect.isGoalie ? (
                                    <>
                                        <div className="blind-draft-info-item">
                                            <span>SV%</span>
                                            <strong>{formatSavePct(selectedTotals?.savePercentage)}</strong>
                                        </div>
                                        <div className="blind-draft-info-item">
                                            <span>GAA</span>
                                            <strong>
                                                {selectedTotals?.goalsAgainstAverage?.toFixed?.(2) ?? "—"}
                                            </strong>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="blind-draft-info-item">
                                            <span>PTS</span>
                                            <strong>{selectedTotals?.points ?? "—"}</strong>
                                        </div>
                                        <div className="blind-draft-info-item">
                                            <span>PPG</span>
                                            <strong>{formatPointsPerGame(selectedTotals?.pointsPerGame)}</strong>
                                        </div>
                                    </>
                                )}
                            </div>
                            <ProspectStatsBreakdown
                                breakdown={selectedTotals?.leagueBreakdown}
                                isGoalie={selectedProspect.isGoalie}
                            />
                            {phase === PHASE.DRAFT && isUserTurn && !draftedIds.includes(selectedProspect.anonId) && (
                                <div style={{ marginTop: 18 }}>
                                    <button
                                        type="button"
                                        className="blind-draft-cta"
                                        onClick={() => handleUserDraft(selectedProspect)}
                                    >
                                        Draft {selectedProspect.fakeName}
                                        {selectedIsStarred ? " ★" : ""}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BlindDraft;
